import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { QuoteEditData } from "../../../hooks/useQuoteEdit";
import { useQuotesStore } from "../../../stores/quotesStore";
import { createEmptyQuoteData } from "../../../utils/documentDefaults";
import { QuoteTemplateRouter } from '@/components/templates/quote/QuoteTemplateRouter';
import { QuoteTemplateEditRouter } from '@/components/templates/quote/editable/QuoteTemplateEditRouter';
import { Button } from '@/components/ui';
import type { QuoteWithRelations, QuoteItem } from '../types/billing.types';

// Wrapper pour créer un nouveau devis
export const QuoteNewRoute: React.FC = () => {
  const emptyQuoteData = createEmptyQuoteData();

  return <QuoteTemplateEditRouter initialData={emptyQuoteData} />;
};

// Wrapper pour éditer un devis existant
export const QuoteEditRoute: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // TODO: Migrer vers useQuery (convention TanStack Query) — Zustand profondément intégré ici (fetch + state + loading + error)
  const { currentQuote, isLoading, error, fetchQuote } = useQuotesStore();

  // Charger le devis si nécessaire
  React.useEffect(() => {
    if (id && id !== "0") {
      fetchQuote(id);
    }
  }, [id, fetchQuote]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="bg-white/60 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/40 p-8">
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600"></div>
            <p className="text-gray-700 font-medium">{t('quotes.list.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !currentQuote) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-6 text-center">
          <p className="text-red-600 font-semibold mb-4">{t('quotes.list.notFound')}</p>
          <Button onClick={() => navigate("/quotes")}>{t('common.backToList')}</Button>
        </div>
      </div>
    );
  }

  // Convertir le devis pour QuoteEditData (nouveau format)
  // Cast vers QuoteWithRelations car l'API retourne les relations Client, Company, QuoteItem
  const cq = currentQuote as unknown as QuoteWithRelations;
  const rawItems = typeof cq.items === "string" ? JSON.parse(cq.items) : (cq.items || cq.QuoteItem || []);

  // Normaliser les items pour avoir tous les champs requis
  const normalizedItems = (rawItems as QuoteItem[]).map((item) => ({
    id: item.id || crypto.randomUUID(),
    description: item.description || "",
    quantity: item.quantity || 0,
    unitPrice: item.unitPrice || 0,
    taxPercent: item.taxPercent ?? 0,
    total: item.totalHT || 0,
    unit: item.unit || undefined,
    reference: item.reference || undefined,
    lineType: item.lineType || 'ITEM' as const,
    groupId: item.groupId || undefined,
    groupTitle: item.groupTitle || undefined,
    discountType: item.discountType || undefined,
    discountValue: item.discountValue ? Number(item.discountValue) : undefined,
    discountAmount: item.discountAmount ? Number(item.discountAmount) : undefined,
    taxRateId: item.taxRateId || undefined,
    productId: item.productId || undefined,
    descriptionType: item.descriptionType || 'COMMERCIAL',
  }));

  const editData: QuoteEditData = {
    id: cq.id,
    quoteNumber: cq.number,
    date: String(cq.date || cq.issueDate || ''),
    validUntil: String(cq.validUntil || ''),

    // ✅ CRITIQUE: clientId obligatoire pour la mise à jour
    clientId: cq.clientId,

    // Client - Utilise UNIQUEMENT la structure Prisma V2 (pas de fallback legacy)
    clientName: cq.Client?.fullName || "",
    clientCompany: cq.Client?.companyName || "",
    clientEmail: cq.Client?.email || "",
    clientAddressLine1: cq.Client?.addressLine1 || "",
    clientCity: cq.Client?.city || "",
    clientZipCode: cq.Client?.zipCode || "",
    clientCountry: cq.Client?.countryCode || "FR",
    clientPhone: cq.Client?.phone || "",
    clientVatNumber: cq.Client?.vatNumber || "",

    // Items et totaux
    items: normalizedItems,
    subtotal: parseFloat(String(cq.totalHT)) || 0,
    tax: parseFloat(String(cq.totalTVA)) || 0,
    total: parseFloat(String(cq.totalTTC)) || 0,

    // Remise
    discountType: ((cq.discountType || "NONE").toLowerCase()) as
      | "none"
      | "percentage"
      | "fixed",
    discountValue: parseFloat(String(cq.discountValue)) || 0,
    discountAmount: parseFloat(String(cq.discountAmount)) || 0,

    // Notes & Titre (alignés sur Prisma)
    title: cq.title || "",
    internalNotes: cq.internalNotes || "",
    termsConditions: cq.termsConditions || "",

    // Company data from relation
    companyName: cq.Company?.legalName || cq.Company?.tradeName || "",
    companyAddressLine1: cq.Company?.addressLine1 || "",
    companyCity: cq.Company?.city || "",
    companyZipCode: cq.Company?.zipCode || "",
    companyCountry: cq.Company?.countryCode || "FR",
    companyPhone: cq.Company?.phone || "",
    companyEmail: cq.Company?.email || "",
    companySiret: cq.Company?.registrationNumber || "",
    companyVatNumber: cq.Company?.vatNumber || "",
  };

  return <QuoteTemplateEditRouter initialData={editData} />;
};

// Wrapper pour voir un devis en lecture seule
export const QuoteViewRoute: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t: tView } = useTranslation();

  // TODO: Migrer vers useQuery (convention TanStack Query) — Zustand profondément intégré ici (fetch + state + loading + error)
  const { currentQuote, isLoading, error, fetchQuote } = useQuotesStore();

  // Charger le devis si nécessaire
  React.useEffect(() => {
    if (id && id !== "0") {
      fetchQuote(id);
    }
  }, [id, fetchQuote]);

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">{tView('common.loading')}</p>
      </div>
    );
  }

  if (error || !currentQuote) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">{tView('quotes.list.notFound')}</p>
        <Button onClick={() => navigate("/quotes")} className="mt-4">
          {tView('common.backToList')}
        </Button>
      </div>
    );
  }

  // Transformer Quote (Prisma) en QuoteData (template)
  // Cast vers QuoteWithRelations car l'API retourne les relations Client et QuoteItem
  const cq = currentQuote as unknown as QuoteWithRelations;
  const quoteData = {
    id: cq.id,
    number: cq.number || '',
    quoteNumber: cq.number || '',
    issueDate: String(cq.issueDate || ''),
    date: String(cq.issueDate || ''),
    validUntil: String(cq.validUntil || ''),
    title: cq.title || '',
    // Passer l'objet Client complet (attendu par QuoteViewTemplate)
    Client: cq.Client,
    // Aussi garder les propriétés plates pour compatibilité
    clientName: cq.Client?.fullName || '',
    clientCompany: cq.Client?.companyName || '',
    clientAddressLine1: cq.Client?.addressLine1 || '',
    clientAddressLine2: cq.Client?.addressLine2 || '',
    clientCity: cq.Client?.city || '',
    clientZipCode: cq.Client?.zipCode || '',
    clientCountryCode: cq.Client?.countryCode || 'FR',
    clientPhone: cq.Client?.phone || '',
    clientEmail: cq.Client?.email || '',
    items: cq.QuoteItem?.map((item: QuoteItem) => ({
      description: item.description || '',
      quantity: item.quantity || 0,
      unitPrice: item.unitPrice || 0,
      total: item.totalHT || 0,
      taxPercent: item.taxPercent || 0,
      unit: item.unit || undefined,
      reference: item.reference || undefined,
      lineType: item.lineType || 'ITEM',
      groupId: item.groupId || undefined,
      groupTitle: item.groupTitle || undefined,
      descriptionType: item.descriptionType || 'COMMERCIAL',
      technicalDescription: (item as any).Product?.technicalDescription || undefined,
      discountType: (item.discountType as any) || 'NONE',
      discountValue: item.discountValue || 0,
      discountAmount: item.discountAmount || 0,
    })) || [],
    subtotal: cq.totalHT || 0,
    totalHT: cq.totalHT || 0,
    tax: cq.totalTVA || 0,
    totalTVA: cq.totalTVA || 0,
    total: cq.totalTTC || 0,
    totalTTC: cq.totalTTC || 0,
    discountType: cq.discountType || 'none',
    discountValue: cq.discountValue || 0,
    discountAmount: cq.discountAmount || 0,
    internalNotes: cq.internalNotes || '',
    notes: cq.internalNotes || '',
    termsConditions: cq.termsConditions || '',
    status: cq.status || '',
  };

  return (
    <QuoteTemplateRouter
      quoteData={quoteData}
      onClose={() => {
        navigate("/quotes");
      }}
    />
  );
};
